import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { FIX_FLOWS } from "@/lib/fixFlows";
import { FIX_GUIDES } from "@/lib/fixGuides";
import { AskAI } from "@/components/nds/AskAI";
import { FixAction, btn } from "@/components/nds/FixAction";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = (method, path, data) => axios({ method, url: `${BASE}${path}`, data, headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });

function Stepper({ steps, at, done }) {
  return (
    <ol className="flex flex-wrap gap-2" data-testid="guided-steps">
      {steps.map((s, i) => <li key={s} data-testid={`guided-step-${i}`} className={`px-2.5 py-1 rounded-full border font-mono text-[9px] tracking-[0.16em] uppercase ${i === at ? "border-nex-gold text-nex-gold bg-nex-gold/10" : done.has(i) || i < at ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-nex-muted"}`}>{done.has(i) ? "✓ " : `${i + 1}. `}{s}</li>)}
    </ol>
  );
}

function CheckFixed({ id, onClose }) {
  const [state, setState] = useState(null);
  const check = async () => { setState("busy"); try { const r = await api("get", `/problems/item/${encodeURIComponent(id)}`); setState(r.data.problem ? "open" : "cleared"); } catch { setState("error"); } };
  return (
    <div className="space-y-3">
      {state !== "cleared" && <button type="button" onClick={check} disabled={state === "busy"} data-testid="guided-check" className={`${btn} border-nex-gold text-nex-gold hover:bg-nex-gold/10`}>
        {state === "busy" && <Loader2 className="h-3 w-3 animate-spin" />} Check it's fixed</button>}
      {state === "cleared" && <p data-testid="guided-cleared" className="flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Fixed. This problem has cleared from the Problems Inbox.</p>}
      {state === "open" && <p data-testid="guided-still-open" className="text-sm text-amber-200">It's still showing as a problem. Go back to the steps above, or ask the AI below what's missing.</p>}
      {state === "error" && <p className="text-sm text-red-300">Couldn't check right now. Try again.</p>}
      <button type="button" onClick={onClose} data-testid="guided-close" className={`${btn} border-white/15 text-nex-muted hover:text-nex-silver`}>Done</button>
    </div>
  );
}

/** "Fix it with me": walks through one problem step by step; every change needs a Confirm; Ask AI on every step. */
export default function FixGuidedContent({ win }) {
  const { problemId } = win.payload || {};
  const code = String(problemId || "").split(":")[0];
  const flow = FIX_FLOWS[code] || [];
  const { openWindow, closeWindow } = useFloatingWindows();
  const [data, setData] = useState(null);
  const [at, setAt] = useState(0);
  const [done, setDone] = useState(() => new Set());
  const [doneActs, setDoneActs] = useState({});
  const [history, setHistory] = useState([]);
  const load = useCallback(() => api("get", `/problems/item/${encodeURIComponent(problemId)}`).then((r) => setData(r.data)).catch(() => setData({ error: true })), [problemId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-6 text-nex-muted flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  if (data.error || !flow.length) return <p className="p-6 text-sm text-red-300" data-testid="guided-error">Couldn't open this guide.</p>;
  const p = data.problem;
  const c = { p, d: data.context, rid: p?.resident_id || p?.ctx?.residentId, name: p?.resident_name || "the resident" };
  if (!p && !done.size) return <p className="p-6 text-sm text-emerald-300" data-testid="guided-already-fixed">This problem is already fixed. Nothing to do.</p>;
  const titles = [...flow.map((s) => s.title), "Check it's fixed"];
  const last = at === flow.length;
  const s = flow[at];
  const target = s?.go?.(c);
  const details = `${p?.title || ""} · ${c.name} · ${p?.detail || ""}`;
  const next = () => { setDone((d) => new Set(d).add(at)); setAt(at + 1); };
  return (
    <div data-testid="fix-guided" className="p-5 space-y-5">
      <div><div className="text-nex-silver font-medium" data-testid="guided-title">{p?.title || FIX_GUIDES[code]?.title}{p?.resident_name ? ` · ${p.resident_name}` : ""}</div>
        <div className="text-xs text-nex-muted">{p?.detail}</div></div>
      <Stepper steps={titles} at={at} done={done} />
      <div data-testid={`guided-body-${at}`} className="space-y-3">
        {last ? <CheckFixed id={problemId} onClose={() => closeWindow(win.id)} /> : <>
          <p className="text-sm text-nex-silver" data-testid="guided-say">{s.say(c)}</p>
          {s.info && <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-sm" data-testid="guided-info">
            {s.info(c).map(([k, v], i) => <React.Fragment key={`${k}-${i}`}><dt className="text-nex-muted text-xs pt-0.5">{k}</dt><dd className="text-nex-silver whitespace-pre-line">{v || "—"}</dd></React.Fragment>)}</dl>}
          {target && <button type="button" onClick={() => openWindow(...target)} data-testid="guided-go" className={`${btn} border-nex-gold/60 text-nex-gold hover:bg-nex-gold/10`}><ArrowUpRight className="h-3 w-3" /> {s.goLabel || "Take me there"}</button>}
          {(s.actions || []).filter((a) => !a.hidden?.(c)).map((a) => <FixAction key={a.id} a={a} c={c} api={api} testid={`guided-action-${a.id}`}
            done={doneActs[`${at}:${a.id}`]} onDone={() => { setDoneActs((o) => ({ ...o, [`${at}:${a.id}`]: true })); window.dispatchEvent(new CustomEvent("problems:changed")); }} />)}
          <div className="flex gap-2 pt-1">
            {at > 0 && <button type="button" onClick={() => setAt(at - 1)} data-testid="guided-back" className={`${btn} border-white/15 text-nex-muted hover:text-nex-silver`}>Back</button>}
            <button type="button" onClick={next} data-testid="guided-next" className={`${btn} border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10`}>
              {(s.actions || []).length && !Object.keys(doneActs).some((k) => k.startsWith(`${at}:`)) ? "Skip this step" : "Next"}</button>
          </div>
        </>}
        <AskAI code={code} title={p?.title || ""} details={details} step={titles[at]} history={history} setHistory={setHistory} testid="guided-ask" />
      </div>
    </div>
  );
}
